import ReactGA from "react-ga4";

// Analytics service class for centralized tracking
class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.gtmLoaded = false;
    this.ga4Loaded = false;
    this.lastPageView = null;
    this.pageViewDebounceTime = 1000; // 1 second debounce
  }

  // Initialize both GA4 and GTM
  initialize() {
    if (this.isInitialized) return;

    try {
      // Initialize GA4
      if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
        ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID, {
          debug: process.env.NODE_ENV === "development",
          gaOptions: {
            siteSpeedSampleRate: 100,
            // Disable all Enhanced Measurement features to prevent scroll tracking
            enhanced_measurement_settings: {
              scrolls: false,
              outbound_clicks: false,
              site_search: false,
              video_engagement: false,
              file_downloads: false,
            },
          },
        });
        this.ga4Loaded = true;
      }

      // Initialize dataLayer for GTM (already loaded in HTML)
      window.dataLayer = window.dataLayer || [];

      // Set up GTM event listeners
      this.setupGTMEvents();
      this.gtmLoaded = true;

      this.isInitialized = true;

      // Set global flag to indicate main analytics is active
      window.__MAIN_ANALYTICS_ACTIVE = true;
    } catch (error) {
      console.error("❌ Analytics initialization failed:", error);
    }
  }

  // Set up GTM-specific events
  setupGTMEvents() {
    // GTM setup complete - page views will be handled by trackPageView calls
  }

  // Enhanced page view tracking
  trackPageView(url, title) {
    if (!this.isInitialized) {
      console.warn("⚠️ Analytics not initialized for page view:", url);
      return;
    }

    const currentPath = url || window.location.pathname;
    const currentTime = Date.now();

    // Check for duplicate page views within debounce time
    if (
      this.lastPageView &&
      this.lastPageView.path === currentPath &&
      currentTime - this.lastPageView.timestamp < this.pageViewDebounceTime
    ) {
      return;
    }

    const pageData = {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: currentPath,
      page_referrer: document.referrer,
      timestamp: new Date().toISOString(),
    };

    // Update last page view tracking
    this.lastPageView = {
      path: currentPath,
      timestamp: currentTime,
    };

    // Always try to send page_view event to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        ...pageData,
      });
    }

    // Also send direct gtag for immediate GA4 tracking
    if (window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID) {
      window.gtag("event", "page_view", pageData);

      // Also send as config for compatibility
      window.gtag("config", process.env.REACT_APP_GA_MEASUREMENT_ID, {
        page_title: pageData.page_title,
        page_location: pageData.page_location,
        page_path: pageData.page_path,
        // Ensure all Enhanced Measurement features remain disabled
        enhanced_measurement_settings: {
          scrolls: false,
          outbound_clicks: false,
          site_search: false,
          video_engagement: false,
          file_downloads: false,
        },
      });
    }

    // Fallback to ReactGA if available
    if (this.ga4Loaded && window.ReactGA) {
      try {
        window.ReactGA.send({
          hitType: "pageview",
          page: pageData.page_path,
          title: pageData.page_title,
        });
      } catch (error) {
        console.warn("⚠️ ReactGA page view failed:", error);
      }
    }
  }

  // Enhanced event tracking
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) {
      console.warn("⚠️ Analytics not initialized for event:", eventName);
      return;
    }

    // Add common parameters
    const eventData = {
      event_timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_location: window.location.href,
      user_agent: navigator.userAgent,
      analytics_source: "main_analytics",
      ...parameters,
    };

    let sentCount = 0;

    // Always send to GTM dataLayer for GTM processing if available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      });
      sentCount++;
    }

    // Always send directly to GA4 via gtag for Tag Assistant visibility if available
    if (window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID) {
      window.gtag("event", eventName, eventData);
      sentCount++;
    }

    // Fallback: ReactGA (only if gtag is not available)
    if (!window.gtag && this.ga4Loaded && window.ReactGA) {
      try {
        ReactGA.event(eventName, eventData);
        sentCount++;
      } catch (error) {
        console.warn("❌ ReactGA failed:", error);
      }
    }

    if (sentCount === 0) {
      console.warn("❌ No analytics method available - event not sent!");
    }
  }

  // User identification and properties
  setUserId(userId) {
    if (!this.isInitialized) return;

    // GA4 user ID
    if (this.ga4Loaded && window.gtag) {
      window.gtag("config", process.env.REACT_APP_GA_MEASUREMENT_ID, {
        user_id: userId,
        // Ensure all Enhanced Measurement features remain disabled
        enhanced_measurement_settings: {
          scrolls: false,
          outbound_clicks: false,
          site_search: false,
          video_engagement: false,
          file_downloads: false,
        },
      });
    }

    // GTM user ID
    if (this.gtmLoaded && window.dataLayer) {
      window.dataLayer.push({
        event: "user_id_set",
        user_id: userId,
      });
    }
  }

  // Set custom user properties
  setUserProperties(properties) {
    if (!this.isInitialized) return;

    // GA4 user properties
    if (this.ga4Loaded && window.gtag) {
      window.gtag("set", "user_properties", properties);
    }

    // GTM user properties
    if (this.gtmLoaded && window.dataLayer) {
      window.dataLayer.push({
        event: "user_properties_set",
        user_properties: properties,
      });
    }
  }

  // E-commerce tracking (AutoCheck specific)
  trackPurchase(transactionData) {
    const purchaseEvent = {
      transaction_id: transactionData.transactionId,
      value: parseFloat(transactionData.value),
      currency: transactionData.currency || "USD",
      items: transactionData.items || [],
      coupon: transactionData.coupon,
      affiliation: "AutoCheck",
      payment_type: transactionData.paymentType,
    };

    this.trackEvent("purchase", purchaseEvent);
  }

  // E-commerce: Add to cart tracking
  trackAddToCart(item) {
    const cartEvent = {
      currency: item.currency || "USD",
      value: parseFloat(item.price || 0),
      items: [
        {
          item_id: item.code || item.id,
          item_name: item.name,
          price: parseFloat(item.price || 0),
          quantity: item.quantity || 1,
          item_category: item.category || "vehicle_report",
        },
      ],
    };

    this.trackEvent("add_to_cart", cartEvent);
  }

  // E-commerce: Begin checkout tracking
  trackBeginCheckout(checkoutData = {}) {
    this.trackCheckoutStep("begin", checkoutData);
  }

  // AutoCheck specific: VIN search tracking
  trackVinSearch(vin, searchType = "vin") {
    this.trackEvent("vin_search", {
      search_term: vin,
      search_type: searchType,
      content_type: "vehicle_report",
    });
  }

  // AutoCheck specific: Report view tracking
  trackReportView(reportData) {
    this.trackEvent("view_item", {
      item_id: reportData.vin,
      item_name: `Vehicle Report - ${reportData.vin}`,
      item_category: "vehicle_report",
      price: reportData.price,
      currency: "USD",
      autocheck_score: reportData.autocheckScore,
      vehicle_year: reportData.year,
      vehicle_make: reportData.make,
      vehicle_model: reportData.model,
    });
  }

  // Form tracking
  trackFormSubmission(formName, formData = {}) {
    this.trackEvent("Form Submit", {
      form_name: formName,
      form_location: window.location.pathname,
      ...formData,
    });
  }

  // Form successful submission tracking
  trackFormSuccessfulSubmit(formName, formData = {}) {
    this.trackEvent("Form Submission - Successful Submit", {
      form_name: formName,
      form_location: window.location.pathname,
      submission_status: "success",
      ...formData,
    });
  }

  // File download tracking
  trackDownload(fileName, fileType) {
    this.trackEvent("file_download", {
      file_name: fileName,
      file_type: fileType,
      link_url: window.location.href,
    });
  }

  // External link tracking
  trackExternalLink(url, linkText) {
    this.trackEvent("click", {
      link_url: url,
      link_text: linkText,
      outbound: true,
    });
  }

  // AutoCheck Score specific interactions
  trackAutocheckScoreInteraction(action, scoreData = {}) {
    this.trackEvent("autocheck_score_interaction", {
      interaction_type: action, // 'view', 'hover', 'click', 'expand', 'compare'
      autocheck_score: scoreData.score,
      vehicle_vin: scoreData.vin,
      vehicle_year: scoreData.year,
      vehicle_make: scoreData.make,
      vehicle_model: scoreData.model,
      score_explanation_viewed: scoreData.explanationViewed || false,
      comparison_count: scoreData.comparisonCount || 0,
      ...scoreData,
    });
  }

  // Sample report tracking
  trackSampleReportView(sampleType, reportData = {}) {
    this.trackEvent("sample_report_view", {
      sample_type: sampleType, // 'full_sample', 'quick_preview', 'score_only'
      report_format: reportData.format || "web", // 'web', 'pdf', 'mobile'
      time_spent: reportData.timeSpent,
      sections_viewed: reportData.sectionsViewed || [],
      conversion_intent: reportData.conversionIntent || "unknown", // 'high', 'medium', 'low'
      referrer_page: document.referrer,
      ...reportData,
    });
  }

  // Partner referral attribution
  trackPartnerReferral(partnerData = {}) {
    // Get partner context from URL and session
    const urlParams = new URLSearchParams(window.location.search);
    const partnerSiteId = JSON.parse(sessionStorage.getItem("partnerSiteId"));

    this.trackEvent("partner_referral", {
      partner_name: partnerData.partnerName || partnerSiteId?.partnerName,
      partner_id: partnerData.partnerId || partnerSiteId?.uid,
      partner_site_id:
        partnerData.siteId ||
        partnerSiteId?.siteId ||
        urlParams.get("siteID") ||
        urlParams.get("siteid") ||
        urlParams.get("siteId") ||
        urlParams.get("SITEID"),
      referral_source: partnerData.source || urlParams.get("utm_source"),
      referral_medium: partnerData.medium || urlParams.get("utm_medium"),
      referral_campaign: partnerData.campaign || urlParams.get("utm_campaign"),
      referral_content: partnerData.content || urlParams.get("utm_content"),
      landing_page: window.location.pathname,
      session_start: partnerData.sessionStart || false,
      revenue_attribution: this.getPartnerRevenueShare(partnerData.partnerId),
      ...partnerData,
    });
  }

  // Subscription vs single report choice tracking
  trackReportChoiceSelection(choiceData = {}) {
    this.trackEvent("report_choice_selection", {
      choice_type: choiceData.type, // 'single_report', '5_reports_21_days', 'unlimited_subscription'
      price_point: parseFloat(choiceData.price),
      currency: choiceData.currency || "USD",
      choice_reason: choiceData.reason, // 'price_sensitive', 'multi_vehicle', 'single_purchase'
      previous_choice: choiceData.previousChoice,
      choice_change_count: choiceData.changeCount || 0,
      time_to_decision: choiceData.timeToDecision, // milliseconds
      comparison_viewed: choiceData.comparisonViewed || false,
      value_proposition_clicked: choiceData.valuePropositionClicked || false,
      ...choiceData,
    });
  }

  // Mobile app interactions (for mobile web or app integration)
  trackMobileAppInteraction(action, appData = {}) {
    this.trackEvent("mobile_app_interaction", {
      action_type: action, // 'download_prompt', 'app_store_click', 'deep_link', 'mobile_optimized_view'
      device_type: navigator.userAgent.includes("Mobile")
        ? "mobile"
        : "desktop",
      app_download_source: appData.downloadSource, // 'app_store', 'google_play', 'direct'
      mobile_optimization: appData.mobileOptimized || false,
      screen_size: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      touch_enabled: "ontouchstart" in window,
      user_agent: navigator.userAgent,
      ...appData,
    });
  }

  // Enhanced partner attribution helper
  getPartnerRevenueShare(partnerId) {
    const partnerShares = {
      cargurus: 0.15,
      cars_com: 0.12,
      autotrader: 0.14,
      edmunds: 0.13,
      kbb: 0.11,
      ebay: 0.1,
      default: 0.1,
    };

    if (!partnerId) return 0;
    const partnerKey = partnerId.toLowerCase().replace(/[^a-z]/g, "_");
    return partnerShares[partnerKey] || partnerShares.default;
  }

  // Performance tracking
  trackTiming(category, variable, value, label) {
    if (window.gtag) {
      window.gtag("event", "timing_complete", {
        name: variable,
        value: value,
        event_category: category,
        event_label: label,
      });
    }
  }

  // Session management and user journey tracking
  trackSessionStart(sessionData = {}) {
    this.trackEvent("session_start", {
      session_id: sessionData.sessionId || this.generateSessionId(),
      referrer: document.referrer || "direct",
      landing_page: window.location.pathname,
      utm_source: new URLSearchParams(window.location.search).get("utm_source"),
      utm_medium: new URLSearchParams(window.location.search).get("utm_medium"),
      utm_campaign: new URLSearchParams(window.location.search).get(
        "utm_campaign",
      ),
      device_type: this.getDeviceType(),
      browser: this.getBrowser(),
      is_returning_user: sessionData.isReturning || false,
      ...sessionData,
    });
  }

  // User authentication tracking
  trackUserAuthentication(action, authData = {}) {
    this.trackEvent("user_authentication", {
      auth_action: action, // 'login', 'logout', 'register', 'forgot_password', 'password_reset'
      auth_method: authData.method || "email", // 'email', 'social', 'phone'
      success: authData.success !== false,
      error_code: authData.errorCode,
      user_type: authData.userType || "consumer", // 'consumer', 'dealer', 'partner'
      account_age: authData.accountAge, // days since registration
      login_attempts: authData.loginAttempts || 1,
      ...authData,
    });
  }

  // Search and filter analytics
  trackSearchInteraction(searchData = {}) {
    this.trackEvent("search_interaction", {
      search_type: searchData.type, // 'vin', 'license_plate', 'make_model', 'advanced'
      search_query: searchData.query,
      search_filters: searchData.filters || [],
      results_count: searchData.resultsCount,
      result_clicked: searchData.resultClicked || false,
      search_refinements: searchData.refinements || 0,
      time_to_first_result: searchData.timeToFirstResult,
      ...searchData,
    });
  }

  // Checkout and payment flow tracking
  trackCheckoutStep(step, checkoutData = {}) {
    this.trackEvent("checkout_progress", {
      checkout_step: step, // 'begin', 'shipping_info', 'payment_info', 'review', 'complete'
      step_number: checkoutData.stepNumber,
      checkout_option: checkoutData.option,
      currency: checkoutData.currency || "USD",
      value: parseFloat(checkoutData.value || 0),
      items: checkoutData.items || [],
      payment_method: checkoutData.paymentMethod,
      shipping_method: checkoutData.shippingMethod,
      discount_amount: checkoutData.discountAmount || 0,
      ...checkoutData,
    });
  }

  // Content engagement tracking
  trackContentEngagement(contentData = {}) {
    this.trackEvent("content_engagement", {
      content_type: contentData.type, // 'article', 'faq', 'guide', 'sample_report', 'score_explanation'
      content_id: contentData.id,
      content_title: contentData.title,
      engagement_type: contentData.engagementType, // 'view', 'scroll', 'click', 'share', 'bookmark'
      engagement_value: contentData.value, // scroll percentage, time spent, etc.
      content_category: contentData.category,
      time_on_content: contentData.timeOnContent,
      scroll_depth: contentData.scrollDepth,
      ...contentData,
    });
  }

  // Error and exception tracking
  trackError(errorData = {}) {
    this.trackEvent("exception", {
      error_type: errorData.type || "javascript_error",
      error_message: errorData.message,
      error_stack: errorData.stack,
      error_location: errorData.location || window.location.pathname,
      user_action: errorData.userAction, // what user was trying to do
      error_severity: errorData.severity || "medium", // 'low', 'medium', 'high', 'critical'
      browser_info: navigator.userAgent,
      timestamp: new Date().toISOString(),
      fatal: errorData.fatal || false,
      ...errorData,
    });
  }

  // Privacy and consent tracking
  trackPrivacyConsent(consentData = {}) {
    this.trackEvent("privacy_consent", {
      consent_type: consentData.type, // 'cookies', 'analytics', 'marketing', 'functional'
      consent_action: consentData.action, // 'accept', 'reject', 'customize', 'withdraw'
      consent_method: consentData.method || "banner", // 'banner', 'settings', 'popup'
      granular_choices: consentData.choices || {},
      legal_basis: consentData.legalBasis || "consent",
      consent_version: consentData.version || "1.0",
      ...consentData,
    });
  }

  // Performance web vitals tracking
  trackWebVitals(vitalsData = {}) {
    this.trackEvent("web_vitals", {
      vital_type: vitalsData.type, // 'CLS', 'FID', 'FCP', 'LCP', 'TTFB'
      vital_value: vitalsData.value,
      vital_rating: vitalsData.rating, // 'good', 'needs-improvement', 'poor'
      page_type: vitalsData.pageType || "unknown",
      connection_type: navigator.connection?.effectiveType || "unknown",
      device_memory: navigator.deviceMemory || "unknown",
      ...vitalsData,
    });
  }

  // A/B testing and experimentation
  trackExperiment(experimentData = {}) {
    this.trackEvent("experiment_exposure", {
      experiment_id: experimentData.id,
      experiment_name: experimentData.name,
      variant_id: experimentData.variantId,
      variant_name: experimentData.variantName,
      experiment_type: experimentData.type || "web", // 'web', 'feature', 'pricing'
      user_bucket: experimentData.bucket,
      exposure_timestamp: new Date().toISOString(),
      ...experimentData,
    });
  }

  // Helper functions
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return "tablet";
    if (
      /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
        userAgent,
      )
    )
      return "mobile";
    return "desktop";
  }

  getBrowser() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari")) return "Safari";
    if (userAgent.includes("Edge")) return "Edge";
    return "Other";
  }

  // Method to clear page view debounce (useful for debugging)
  clearPageViewDebounce() {
    this.lastPageView = null;
  }

  // Comprehensive analytics test function
  testAllTags() {
    console.group("🧪 Testing All Analytics Tags");

    // Initialize analytics if not already initialized
    if (!this.isInitialized) {
      this.initialize();
    }

    const results = {
      initialization: this.isInitialized,
      gtm: this.gtmLoaded,
      ga4: this.ga4Loaded,
      dataLayer: !!window.dataLayer,
      gtag: !!window.gtag,
      gtmContainer: !!window.google_tag_manager,
      reactGA: !!window.ReactGA,
      tests: {},
    };

    // Test 1: Page View
    try {
      this.trackPageView("/test-page", "Test Page");
      results.tests.pageView = "✅ Sent";
    } catch (error) {
      results.tests.pageView = "❌ Failed: " + error.message;
    }

    // Test 2: Custom Event
    try {
      this.trackEvent("test_event", {
        test_parameter: "test_value",
        timestamp: new Date().toISOString(),
      });
      results.tests.customEvent = "✅ Sent";
    } catch (error) {
      results.tests.customEvent = "❌ Failed: " + error.message;
    }

    // Test 3: VIN Search
    try {
      this.trackVinSearch("TEST123456789VIN", "test");
      results.tests.vinSearch = "✅ Sent";
    } catch (error) {
      results.tests.vinSearch = "❌ Failed: " + error.message;
    }

    // Test 4: Form Submission
    try {
      this.trackFormSubmission("test_form", {
        test_field: "test_value",
      });
      results.tests.formSubmission = "✅ Sent";
    } catch (error) {
      results.tests.formSubmission = "❌ Failed: " + error.message;
    }

    // Test 4b: Form Successful Submission
    try {
      this.trackFormSuccessfulSubmit("test_form", {
        test_field: "test_value",
      });
      results.tests.formSuccessfulSubmission = "✅ Sent";
    } catch (error) {
      results.tests.formSuccessfulSubmission = "❌ Failed: " + error.message;
    }

    // Test 5: E-commerce Event
    try {
      this.trackPurchase({
        transaction_id: "TEST_" + Date.now(),
        value: 29.99,
        currency: "USD",
        items: [
          {
            item_id: "test_report",
            item_name: "Test Vehicle Report",
            price: 29.99,
            quantity: 1,
          },
        ],
      });
      results.tests.purchase = "✅ Sent";
    } catch (error) {
      results.tests.purchase = "❌ Failed: " + error.message;
    }

    // Test 6: License Plate Search Events
    try {
      this.trackEvent("license_plate_search", {
        plate_number: "TEST123",
        state: "CA",
        search_location: "/test",
      });
      results.tests.licensePlateSearch = "✅ Sent";
    } catch (error) {
      results.tests.licensePlateSearch = "❌ Failed: " + error.message;
    }

    // Test 7: License Plate Search Success
    try {
      this.trackEvent("license_plate_search_success", {
        plate_number: "TEST123",
        state: "CA",
        vin: "TEST123456789VIN123",
        vehicle_year: "2020",
        vehicle_make: "Honda",
        vehicle_model: "Civic",
        search_location: "/test",
      });
      results.tests.licensePlateSearchSuccess = "✅ Sent";
    } catch (error) {
      results.tests.licensePlateSearchSuccess = "❌ Failed: " + error.message;
    }

    // Test 8: File Download
    try {
      this.trackDownload("test_report.pdf", "pdf");
      results.tests.fileDownload = "✅ Sent";
    } catch (error) {
      results.tests.fileDownload = "❌ Failed: " + error.message;
    }

    // Test 9: External Link Click
    try {
      this.trackExternalLink("https://example.com", "Test Link");
      results.tests.externalLink = "✅ Sent";
    } catch (error) {
      results.tests.externalLink = "❌ Failed: " + error.message;
    }

    // Test 10: AutoCheck Score Interaction
    try {
      this.trackAutocheckScoreInteraction("view", {
        score: 85,
        vin: "TEST123456789VIN123",
        year: "2020",
        make: "Honda",
        model: "Civic",
        explanationViewed: true,
      });
      results.tests.autocheckScoreInteraction = "✅ Sent";
    } catch (error) {
      results.tests.autocheckScoreInteraction = "❌ Failed: " + error.message;
    }

    // Test 11: Sample Report View
    try {
      this.trackSampleReportView("full_sample", {
        format: "web",
        timeSpent: 45000,
        sectionsViewed: ["summary", "accidents", "ownership"],
        conversionIntent: "high",
      });
      results.tests.sampleReportView = "✅ Sent";
    } catch (error) {
      results.tests.sampleReportView = "❌ Failed: " + error.message;
    }

    // Test 12: Partner Referral
    try {
      this.trackPartnerReferral({
        partnerName: "CarGurus",
        partnerId: "cargurus",
        source: "cargurus.com",
        medium: "referral",
        sessionStart: true,
      });
      results.tests.partnerReferral = "✅ Sent";
    } catch (error) {
      results.tests.partnerReferral = "❌ Failed: " + error.message;
    }

    // Test 13: Report Choice Selection
    try {
      this.trackReportChoiceSelection({
        type: "single_report",
        price: 29.99,
        reason: "single_purchase",
        timeToDecision: 15000,
        comparisonViewed: true,
      });
      results.tests.reportChoiceSelection = "✅ Sent";
    } catch (error) {
      results.tests.reportChoiceSelection = "❌ Failed: " + error.message;
    }

    // Test 14: Mobile App Interaction
    try {
      this.trackMobileAppInteraction("download_prompt", {
        downloadSource: "app_store",
        mobileOptimized: true,
      });
      results.tests.mobileAppInteraction = "✅ Sent";
    } catch (error) {
      results.tests.mobileAppInteraction = "❌ Failed: " + error.message;
    }

    // Test 15: Session Management
    try {
      this.trackSessionStart({
        isReturning: false,
        sessionId: "test_session_123",
      });
      results.tests.sessionStart = "✅ Sent";
    } catch (error) {
      results.tests.sessionStart = "❌ Failed: " + error.message;
    }

    // Test 16: User Authentication
    try {
      this.trackUserAuthentication("login", {
        method: "email",
        success: true,
        userType: "consumer",
      });
      results.tests.userAuthentication = "✅ Sent";
    } catch (error) {
      results.tests.userAuthentication = "❌ Failed: " + error.message;
    }

    // Test 17: Search Interaction
    try {
      this.trackSearchInteraction({
        type: "vin",
        query: "1234567890",
        resultsCount: 1,
        timeToFirstResult: 1500,
      });
      results.tests.searchInteraction = "✅ Sent";
    } catch (error) {
      results.tests.searchInteraction = "❌ Failed: " + error.message;
    }

    // Test 18: Checkout Progress
    try {
      this.trackCheckoutStep("begin", {
        stepNumber: 1,
        value: 29.99,
        items: [{ item_id: "report_001", price: 29.99 }],
      });
      results.tests.checkoutProgress = "✅ Sent";
    } catch (error) {
      results.tests.checkoutProgress = "❌ Failed: " + error.message;
    }

    // Test 19: Content Engagement
    try {
      this.trackContentEngagement({
        type: "sample_report",
        id: "sample_001",
        engagementType: "view",
        timeOnContent: 45000,
        scrollDepth: 75,
      });
      results.tests.contentEngagement = "✅ Sent";
    } catch (error) {
      results.tests.contentEngagement = "❌ Failed: " + error.message;
    }

    // Test 20: Error Tracking
    try {
      this.trackError({
        type: "network_error",
        message: "Failed to load vehicle data",
        severity: "medium",
        userAction: "searching_vehicle",
      });
      results.tests.errorTracking = "✅ Sent";
    } catch (error) {
      results.tests.errorTracking = "❌ Failed: " + error.message;
    }

    // Test 21: Privacy Consent
    try {
      this.trackPrivacyConsent({
        type: "analytics",
        action: "accept",
        method: "banner",
        version: "1.0",
      });
      results.tests.privacyConsent = "✅ Sent";
    } catch (error) {
      results.tests.privacyConsent = "❌ Failed: " + error.message;
    }

    // Test 22: Web Vitals
    try {
      this.trackWebVitals({
        type: "LCP",
        value: 2.5,
        rating: "good",
        pageType: "homepage",
      });
      results.tests.webVitals = "✅ Sent";
    } catch (error) {
      results.tests.webVitals = "❌ Failed: " + error.message;
    }

    // Test 23: A/B Testing
    try {
      this.trackExperiment({
        id: "pricing_test_001",
        name: "Pricing Strategy Test",
        variantId: "variant_a",
        variantName: "Standard Pricing",
        type: "pricing",
      });
      results.tests.experimentTracking = "✅ Sent";
    } catch (error) {
      results.tests.experimentTracking = "❌ Failed: " + error.message;
    }

    // Check recent dataLayer events
    if (window.dataLayer && window.dataLayer.length > 0) {
      const recentEvents = window.dataLayer.slice(-10);
      results.recentDataLayerEvents = recentEvents
        .map((event) => event.event || "config")
        .filter(Boolean);
    }

    // Summary
    const passedTests = Object.values(results.tests).filter((result) =>
      result.includes("✅"),
    ).length;
    const totalTests = Object.keys(results.tests).length;

    if (passedTests === totalTests) {
    } else {
      console.warn(
        "⚠️ Some analytics tags have issues - check the results above",
      );
    }

    console.groupEnd();
    return results;
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

// Make debug function available globally for easy debugging
window.debugAnalytics = () => analytics.debugStatus();
window.clearPageViewDebounce = () => analytics.clearPageViewDebounce();
window.testAllTags = () => analytics.testAllTags();
window.initializeAnalytics = () => analytics.initialize();

// Legacy exports for backward compatibility
export const initGA = () => analytics.initialize();
export const trackPageView = (url, title) =>
  analytics.trackPageView(url, title);
export const trackEvent = ({ category, action, label, value, ...params }) => {
  // Convert legacy format to new format
  analytics.trackEvent(action, {
    event_category: category,
    event_label: label,
    value: value,
    ...params,
  });
};

// New exports
export default analytics;
export { analytics };
